// src/auth/ProtectedByPathModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, Navigate, useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { ROUTE_RULES, PROMO_UNLOCKS } from "./gateConfig";
import AccessDeniedModal from "../components/AccessDeniedModal.jsx";
import { NASCAR_ALL } from "./plans";

/* ---------- helpers ---------- */
const planLabel = (p) => (p ? p.replaceAll("_", " ") : "");

function sportFromPath(pathname) {
  if (pathname.startsWith("/nfl")) return "NFL";
  if (pathname.startsWith("/nascar")) return "NASCAR";
  if (pathname.startsWith("/mlb")) return "MLB";
  if (pathname.startsWith("/nba")) return "NBA";
  return null;
}

/*
  PRO tool paths:
  - Optimizers
  - Contest Simulator
*/
const isProToolPath = (p) =>
  p.includes("/optimizer") ||
  p.includes("/contest-simulator") ||
  p.includes("/contest-sim");

function firstMatchingRule(pathname) {
  return ROUTE_RULES.find((r) => pathname.startsWith(r.prefix)) || null;
}

// prefix match helper: "/" matches only exact "/", all others match prefix
function pathMatches(prefix, pathname) {
  if (prefix === "/") return pathname === "/";
  return pathname === prefix || pathname.startsWith(prefix + "/");
}

/** ALWAYS-PUBLIC routes — no paywall, no auth redirect here. */
const PUBLIC_PATHS = ["/", "/login", "/signup", "/pricing", "/account", "/dashboard", "/discord"];
const isPublicPath = (pathname) => PUBLIC_PATHS.some((p) => pathMatches(p, pathname));
const isPromoOpen = (pathname) =>
  Object.entries(PROMO_UNLOCKS).some(([prefix, open]) => open && pathMatches(prefix, pathname));

/* ---------- Article access rules ---------- */
// Public browse page:
const isCupArticlesIndex = (p) => p === "/nascar/cup/articles" || p === "/nascar/cup/articles/";
// Individual article slug page:
const matchCupArticleSlug = (p) => p.match(/^\/nascar\/cup\/articles\/([^/]+)\/?$/);

// Index JSON that contains free:true/false
const CUP_ARTICLE_INDEX_URL = "/data/nascar/cup/articles/index.json";

// cache to avoid refetching
let cupIndexCache = null;
let cupIndexPromise = null;

async function loadCupIndex() {
  if (cupIndexCache) return cupIndexCache;
  if (!cupIndexPromise) {
    cupIndexPromise = fetch(CUP_ARTICLE_INDEX_URL, { cache: "no-cache" })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => {
        cupIndexCache = Array.isArray(data) ? data : [];
        return cupIndexCache;
      })
      .catch(() => []);
  }
  return cupIndexPromise;
}

async function isFreeCupArticlePath(pathname) {
  const m = matchCupArticleSlug(pathname);
  if (!m) return false;

  const slug = m[1];
  const list = await loadCupIndex();
  const hit = list.find((a) => a?.slug === slug);
  return Boolean(hit?.free);
}

export default function ProtectedByPathModal({ children }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  // IMPORTANT: hooks must always run; do NOT early-return before hooks.
  const [loading, setLoading] = useState(true);
  const [authed, setAuthed] = useState(false);
  const [allowed, setAllowed] = useState(false);
  const [profile, setProfile] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const rule = useMemo(() => firstMatchingRule(pathname), [pathname]);
  const baseAllowList = rule?.allow ?? [];

  const alwaysPublic =
    isPublicPath(pathname) || isPromoOpen(pathname) || isCupArticlesIndex(pathname);

  const isCupArticleSlug = Boolean(matchCupArticleSlug(pathname));

  useEffect(() => {
    let cancelled = false;

    (async () => {
      // reset on every navigation
      setLoading(true);
      setShowModal(false);
      setAllowed(false);

      // Always-public pages just pass through instantly
      if (alwaysPublic) {
        if (cancelled) return;
        setAuthed(true);
        setAllowed(true);
        setLoading(false);
        return;
      }

      // If this is a Cup article slug, check free flag first
      if (isCupArticleSlug) {
        const free = await isFreeCupArticlePath(pathname);
        if (cancelled) return;

        if (free) {
          // Free article: allow for everyone (even logged out)
          setAuthed(true);
          setAllowed(true);
          setLoading(false);
          return;
        }
        // Paid article: continue into auth/plan checks (below)
      }

      // Normal auth gate flow
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (cancelled) return;

      if (!user) {
        setAuthed(false);
        setAllowed(false);
        setLoading(false);

        // For paid Cup article slugs, show upgrade modal instead of redirecting to login
        if (isCupArticleSlug) setShowModal(true);
        return;
      }

      setAuthed(true);

      const { data } = await supabase
        .from("profiles")
        .select("plan,status")
        .eq("id", user.id)
        .single();

      if (cancelled) return;

      setProfile(data || null);

      const active = data?.status === "active" || data?.status === "trialing";

      // If this is a PAID Cup article slug, require NASCAR access specifically.
      const effectiveAllowList = isCupArticleSlug ? NASCAR_ALL : baseAllowList;

      const inPlan =
        effectiveAllowList.length === 0 || effectiveAllowList.includes(data?.plan);

      const ok = Boolean(active && inPlan);

      setAllowed(ok);
      setLoading(false);
      if (!ok) setShowModal(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [alwaysPublic, isCupArticleSlug, pathname, baseAllowList]);

  // While deciding access, don't render a blank white page
  if (loading) {
    return (
      <div style={{ minHeight: "100vh" }}>
        {children}
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.25)",
            backdropFilter: "blur(6px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              padding: "14px 16px",
              borderRadius: 14,
              background: "rgba(10, 12, 18, 0.85)",
              border: "1px solid rgba(255,255,255,0.08)",
              color: "white",
              fontWeight: 700,
            }}
          >
            Loading…
          </div>
        </div>
      </div>
    );
  }

  // If allowed, show the page
  if (allowed) return children;

  // If not authed:
  // - paid Cup articles show modal (handled via showModal)
  // - everything else requires login
  if (!authed && !isCupArticleSlug) return <Navigate to="/login" replace />;

  // Otherwise, show upgrade modal
  const sport = sportFromPath(pathname);
  const proTool = isProToolPath(pathname);

  let primaryNeeded;
  let secondaryAllAccess;

  if (sport) {
    if (proTool) {
      primaryNeeded = `${sport}_PRO`;
      secondaryAllAccess = "ALL_ACCESS_PRO";
    } else {
      primaryNeeded = `${sport}_LITE`;
      secondaryAllAccess = "ALL_ACCESS_LITE";
    }
  } else {
    primaryNeeded = "ALL_ACCESS_LITE";
    secondaryAllAccess = "ALL_ACCESS_PRO";
  }

  const toSlug = (token) => (token ? token.toLowerCase() : "");
  const buyHref = (token) =>
    `/pricing?plan=${encodeURIComponent(toSlug(token))}&from=${encodeURIComponent(pathname)}`;

  const actions = [
    { label: `Get ${planLabel(primaryNeeded)}`, href: buyHref(primaryNeeded) },
    { label: `Get ${planLabel(secondaryAllAccess)}`, href: buyHref(secondaryAllAccess) },
    { label: "See all plans", href: "/pricing" },
  ];

  const youHave = profile?.plan
    ? `Your current plan: ${planLabel(profile.plan)} (${profile.status || "inactive"})`
    : null;

  return (
    <>
      <AccessDeniedModal
        open={showModal}
        onClose={() => navigate("/", { replace: true })}
        title={
          sport
            ? `This page requires ${proTool ? `${sport} PRO` : `${sport} LITE or PRO`}`
            : "Upgrade required"
        }
        description={
          youHave
            ? `${youHave}. Upgrade to unlock this page.`
            : `Please upgrade to unlock this page.`
        }
        actions={actions}
      />
      <div />
    </>
  );
}